// Base layers backed by Google's Map Tiles API (plain {z}/{x}/{y} raster tiles).
//
// The previous base layers used leaflet.gridlayer.googlemutant, which does not
// fetch tiles at all: it creates a second, hidden Google map and scrapes the
// <img> tiles Google draws into it. Google increasingly renders maps to a canvas
// with no tile images at all, and renders nothing whatsoever for a map created
// inside a hidden container — which is why the edit-field modal's map was blank
// while the dashboard's worked, and why it was intermittent elsewhere.
//
// The Map Tiles API is a normal HTTP tile service, so a missing tile is a
// visible failed request rather than a silent scraping miss.
//
// Requires the "Map Tiles API" to be enabled on the Google Cloud project the key
// belongs to. It is a different API — and a different SKU — from the Maps
// JavaScript API, and needs a key that is enabled for it.

var L = require('leaflet');

const CREATE_SESSION_URL = 'https://tile.googleapis.com/v1/createSession';
const TILE_URL = 'https://tile.googleapis.com/v1/2dtiles/{z}/{x}/{y}';

// Renew a little before Google's own expiry so a tile request never races it.
const SESSION_EXPIRY_MARGIN_MS = 5 * 60 * 1000;

// The Map Tiles API key. A browser key is inherently public — it ships in the
// JS bundle and in every tile-request URL — so it is committed here rather than
// hidden, exactly as the old Maps JavaScript API key was in index.html. It is
// kept safe by HTTP-referrer and API restrictions on the Google Cloud project,
// not by being secret. process.env.MAP_TILES_KEY (injected by webpack) can
// override it if an environment ever needs a differently-restricted key.
const DEFAULT_MAP_TILES_KEY = 'AIzaSyDneapTmjucx8dFMfR8Avk5PCZ1Cyir3TI';

function getApiKey() {
  return process.env.MAP_TILES_KEY || DEFAULT_MAP_TILES_KEY;
}

// One in-flight/active session per map type, shared by every map on the page.
const sessions = {};

function requestSession(mapType) {
  const key = getApiKey();
  if (!key) {
    return Promise.reject(new Error('Google Maps API key not found'));
  }

  // 'satellite' + the roadmap layer is Google's equivalent of the old 'hybrid'
  // map type: imagery with roads and labels drawn on top. Google also rejects a
  // terrain session that has no roadmap layer ("the terrain map type must always
  // contain a roadmap layer").
  const body = {
    mapType: mapType === 'hybrid' ? 'satellite' : mapType,
    language: 'en-US',
    region: 'US',
  };
  if (mapType === 'hybrid' || mapType === 'terrain') {
    body.layerTypes = ['layerRoadmap'];
  }

  return fetch(CREATE_SESSION_URL + '?key=' + encodeURIComponent(key), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
    .then((res) => {
      if (!res.ok) {
        return res.text().then((text) => {
          throw new Error(
            'createSession failed (' + res.status + '): ' + text.slice(0, 200),
          );
        });
      }
      return res.json();
    })
    .then((data) => ({
      token: data.session,
      // Google returns expiry as epoch seconds, as a string.
      expiresAt: Number(data.expiry) * 1000,
      key,
    }));
}

function getSession(mapType) {
  const cached = sessions[mapType];
  const stillValid =
    cached &&
    cached.value &&
    cached.value.expiresAt - SESSION_EXPIRY_MARGIN_MS > Date.now();

  if (stillValid) {
    return Promise.resolve(cached.value);
  }
  if (cached && cached.promise) {
    return cached.promise;
  }

  const promise = requestSession(mapType)
    .then((value) => {
      sessions[mapType] = { value };
      return value;
    })
    .catch((err) => {
      // Drop the cache entry so a later map can retry (e.g. once the API is
      // enabled) instead of being stuck with a rejected promise forever.
      delete sessions[mapType];
      throw err;
    });

  sessions[mapType] = { promise };
  return promise;
}

// A TileLayer that only knows its tile URL once the session token arrives.
// Tiles requested before then are held back and drawn on the redraw below.
const GoogleTileLayer = L.TileLayer.extend({
  initialize: function (mapType, options) {
    this._mapType = mapType;
    this._session = undefined;
    L.TileLayer.prototype.initialize.call(this, TILE_URL, options);
  },

  onAdd: function (map) {
    L.TileLayer.prototype.onAdd.call(this, map);

    if (this._session) {
      return;
    }
    getSession(this._mapType)
      .then((session) => {
        this._session = session;
        if (this._map) {
          this.redraw();
        }
      })
      .catch((err) => {
        console.error('Google Map Tiles session failed:', err.message);
      });
  },

  getTileUrl: function (coords) {
    if (!this._session) {
      // Leaflet has no "not ready" tile; an empty image keeps it from logging a
      // load error, and redraw() re-requests once the session lands.
      return L.Util.emptyImageUrl;
    }
    return (
      L.Util.template(this._url, {
        z: this._getZoomForUrl(),
        x: coords.x,
        y: coords.y,
      }) +
      '?session=' +
      encodeURIComponent(this._session.token) +
      '&key=' +
      encodeURIComponent(this._session.key)
    );
  },
});

function createGoogleTileLayer(mapType, options) {
  return new GoogleTileLayer(
    mapType,
    Object.assign(
      {
        maxZoom: 21,
        maxNativeZoom: 21,
        // Google requires their attribution to be displayed alongside the tiles.
        attribution: '&copy; Google',
      },
      options,
    ),
  );
}

module.exports = {
  createGoogleTileLayer,
};
